import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, languageNames } from '../i18n/types';
import type { SupportedLanguage, LanguageOption } from '../i18n/types';

/**
 * Custom hook for language operations
 * Implements Requirements 1.4, 2.4
 * 
 * Provides:
 * - currentLanguage: The currently active language
 * - changeLanguage: Function to change the language
 * - languages: Array of all available language options
 * - isChanging: Loading state during language transitions
 */

/**
 * Return type for useLanguage hook
 */
export interface UseLanguageReturn {
  currentLanguage: SupportedLanguage;
  changeLanguage: (lang: SupportedLanguage) => Promise<void>;
  languages: LanguageOption[];
  isChanging: boolean;
}

/**
 * Language options array with native names
 */
const languageOptions: LanguageOption[] = supportedLanguages.map(code => ({
  code,
  name: code.toUpperCase(),
  nativeName: languageNames[code]
}));

/**
 * Custom hook for managing language state and operations
 * 
 * @returns {UseLanguageReturn} Language state and operations
 * 
 * @example
 * ```tsx
 * const { currentLanguage, changeLanguage, languages, isChanging } = useLanguage();
 * 
 * // Change language
 * await changeLanguage('ru');
 * 
 * // Display language options
 * languages.map(lang => (
 *   <button onClick={() => changeLanguage(lang.code)}>
 *     {lang.nativeName}
 *   </button>
 * ))
 * ```
 */
export const useLanguage = (): UseLanguageReturn => {
  const { i18n } = useTranslation();
  const [isChanging, setIsChanging] = useState(false);

  // Get current language from i18next
  // Ensure it's a supported language, fallback to 'en' if not
  const currentLanguage = (
    supportedLanguages.includes(i18n.language as SupportedLanguage)
      ? i18n.language
      : 'uz'
  ) as SupportedLanguage;

  /**
   * Change the application language
   * Requirement 1.4: Language change updates all displayed text within 300ms
   * Requirement 2.4: Language selection changes active locale immediately
   * Requirement 1.5: Language preference persists to localStorage
   * Requirement 7.1: Write to localStorage with key "i18n_locale"
   * Requirement 7.4: Handle localStorage unavailable errors gracefully
   * 
   * @param lang - The language code to switch to
   */
  const changeLanguage = useCallback(async (lang: SupportedLanguage) => {
    // Validate language is supported
    if (!supportedLanguages.includes(lang)) {
      console.warn(`Unsupported language: ${lang}. Ignoring language change.`);
      return;
    }

    // Don't change if already on this language
    if (lang === currentLanguage) {
      return;
    }

    try {
      setIsChanging(true);
      
      // Change language using i18next
      // This will automatically update localStorage via the language detector
      await i18n.changeLanguage(lang);
      
      // Explicitly write to localStorage for redundancy and error handling
      // Requirement 7.1: Use key "i18n_locale" for storage
      // Requirement 7.4: Handle localStorage unavailable errors gracefully
      try {
        localStorage.setItem('i18n_locale', lang);
      } catch (storageError) {
        // localStorage might be unavailable (private browsing, quota exceeded, etc.)
        // Log the error but don't fail the language change
        console.warn('Failed to persist language preference to localStorage:', storageError);
        // Language change still works, just won't persist across sessions
      }
      
      // Small delay to ensure smooth transition
      // This helps with animation timing
      await new Promise(resolve => setTimeout(resolve, 50));
      
    } catch (error) {
      console.error('Failed to change language:', error);
      // Don't throw - fail gracefully
    } finally {
      setIsChanging(false);
    }
  }, [i18n, currentLanguage]);

  // Sync isChanging state with i18next's internal state
  useEffect(() => {
    const handleLanguageChanged = () => {
      setIsChanging(false);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  return {
    currentLanguage,
    changeLanguage,
    languages: languageOptions,
    isChanging
  };
};

export default useLanguage;
